import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { CreditCard, Plus, Minus, Check, Receipt } from 'lucide-react';
import CurrencyInput, { useCurrencyFormatter } from '../../components/common/CurrencyInput';

// Installments counter component
const InstallmentsCounter = ({ value, onChange, max = 24 }) => {
  const increment = () => {
    if (value < max) onChange(value + 1);
  };
  
  const decrement = () => {
    if (value > 1) onChange(value - 1);
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={decrement}
        disabled={value <= 1}
        className="h-10 w-10 rounded-full"
      >
        <Minus className="w-4 h-4" />
      </Button>
      <div className="w-16 text-center">
        <span className="text-2xl font-bold">{value}</span>
        <p className="text-xs text-slate-500">parcela{value > 1 ? 's' : ''}</p>
      </div>
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={increment}
        disabled={value >= max}
        className="h-10 w-10 rounded-full"
      >
        <Plus className="w-4 h-4" />
      </Button>
    </div>
  );
};

const TransportCostModal = ({ 
  open, 
  onOpenChange, 
  tripId,
  transportType,
  direction,
  currency = 'BRL',
  onSave
}) => {
  const { t, language } = useLanguage();
  const [amount, setAmount] = useState(0);
  const [installments, setInstallments] = useState(1);
  const [isPaid, setIsPaid] = useState(false);
  const { format: formatCurrency } = useCurrencyFormatter(currency);

  // Reset state when modal opens
  React.useEffect(() => {
    if (open) {
      setAmount(0);
      setInstallments(1);
      setIsPaid(false);
    }
  }, [open]);

  const directionLabel = direction === 'return' 
    ? t('myTrip.transport.return') 
    : t('myTrip.transport.outbound');

  const handleSave = () => {
    if (amount <= 0) return;
    
    onSave({
      amount,
      installments,
      isPaid,
      currency
    });
    onOpenChange(false);
  };

  const installmentAmount = amount > 0 && installments > 0 ? amount / installments : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto !p-0">
        <DialogHeader className="p-4 sm:p-6 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <CreditCard className="w-5 h-5 text-orange-500" />
            {t('myTrip.transportCost.title')} - {directionLabel}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {t('myTrip.transportCost.description')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-5 px-4 sm:px-6 pb-4">
          {/* Amount */}
          <div>
            <Label className="text-sm sm:text-base">{t('myTrip.transportCost.amount')}</Label>
            <p className="text-xs sm:text-sm text-slate-500 mb-2">{t('myTrip.transportCost.amountDesc')}</p>
            <CurrencyInput
              value={amount}
              onChange={setAmount}
              currency={currency}
              data-testid="transport-cost-amount"
            />
          </div>

          {/* Installments */}
          <div>
            <Label className="text-sm sm:text-base">{t('myTrip.transportCost.installments')}</Label>
            <p className="text-xs sm:text-sm text-slate-500 mb-3">{t('myTrip.transportCost.installmentsDesc')}</p>
            <div className="flex items-center justify-center">
              <InstallmentsCounter
                value={installments}
                onChange={setInstallments}
              />
            </div>
          </div>

          {/* Summary */}
          {amount > 0 && (
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 sm:p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">{t('myTrip.transportCost.total')}</span>
                <span className="font-semibold">{formatCurrency(amount)}</span>
              </div>
              {installments > 1 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">{installments}x de</span>
                  <span className="font-semibold text-orange-500">{formatCurrency(installmentAmount)}</span>
                </div>
              )}
            </div>
          )}

          {/* Already paid toggle */}
          {installments === 1 && (
            <div className="flex items-center justify-between p-3 sm:p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-sm">{t('myTrip.transportCost.alreadyPaid')}</p>
                  <p className="text-xs text-slate-500">{t('myTrip.transportCost.alreadyPaidDesc')}</p>
                </div>
              </div>
              <Switch
                checked={isPaid}
                onCheckedChange={setIsPaid}
                className="flex-shrink-0"
              />
            </div>
          )}

          {/* What will happen info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Receipt className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs sm:text-sm text-blue-700 dark:text-blue-300">
                {installments === 1 
                  ? t('myTrip.transportCost.willCreateSingle')
                  : t('myTrip.transportCost.willCreateMultiple', { count: installments })
                }
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 p-4 sm:p-6 pt-2 border-t">
          <Button
            variant="outline"
            className="flex-1 rounded-full order-2 sm:order-1"
            onClick={() => onOpenChange(false)}
          >
            {t('myTrip.transportCost.skip')}
          </Button>
          <Button
            className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600 order-1 sm:order-2"
            onClick={handleSave}
            disabled={amount <= 0}
          >
            {t('myTrip.transportCost.addPayment')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TransportCostModal;
